import { ApolloError } from '@apollo/client';
import { TableColumnsType } from 'antd';
import { tableSortingMethods } from '../../utils';
import TableV3, { ModifyVariablesArgs } from '../generic/Table/TableV3';
import QueryErrorBoundary from '../error-handling';
import type { ITApplicationDescription } from '../../hooks/business-functions/types';
import { CollapsibleText } from '../generic/TextTruncation';

type ApplicationDescriptionTableProps = {
  itAppsData: ITApplicationDescription[];
  loading: boolean;
  error: ApolloError | undefined;
  businessFunctionId: string | undefined;
  updateITApplicationComment: any;
  locationId?: string;
};

export const ApplicationDescriptionTable = ({
  itAppsData,
  loading,
  error,
  businessFunctionId,
  updateITApplicationComment,
  locationId,
}: ApplicationDescriptionTableProps) => {
  const columns: TableColumnsType<ITApplicationDescription> = [
    {
      title: 'Applications',
      width: '25%',
      dataIndex: 'application',
      key: 'application',
      fixed: 'left',
      defaultSortOrder: 'ascend',
      sorter: (a, b) =>
        tableSortingMethods.string(a.application, b.application),
      render: (text: string) => (
        <span className="custom-table--column ml-10 truncate">{text}</span>
      ),
    },
    {
      title: 'Application Description',
      dataIndex: 'mappingComment',
      key: 'mappingComment',
      width: '75%',
      render: (text: string) => <CollapsibleText text={text} />,
    },
  ];

  const formFields = [
    {
      key: 'mappingComment',
      label: 'Application Description',
      type: 'textArea',
    },
  ];

  // Update function that handles both types of comments
  const updateApplicationComment = async (options: any) => {
    const { variables } = options;

    return updateITApplicationComment({
      variables: {
        data: {
          businessFunctionUuid: businessFunctionId,
          uuid: variables?.data?.itApplicationUuid,
          comment: variables?.data?.comment,
          ...(locationId && { locationUuid: locationId }),
        },
      },
    });
  };

  const modifyVariables = ({ values, currentRecord }: ModifyVariablesArgs) => {
    return {
      businessFunctionUuid: businessFunctionId,
      itApplicationUuid: currentRecord?.uuid,
      comment: values?.mappingComment,
      ...(locationId && { locationUuid: locationId }),
    };
  };

  return (
    <QueryErrorBoundary error={error}>
      <TableV3
        columns={columns}
        data={itAppsData}
        customStyles="nested-columns--custom table-fixed w-full"
        useBorderedSection
        updateGQL={updateApplicationComment}
        loading={loading}
        tableSize="small"
        editable
        editableOptions={{
          update: true,
        }}
        inlineEditing
        formFields={formFields}
        modifyVariables={modifyVariables}
        scroll={{ x: true }}
      />
    </QueryErrorBoundary>
  );
};
